import type { IrReport } from "./types";

export const G3_IR_REPORTS: IrReport[] = [
  {
    key: "G3R1",
    group: "G3",
    rCode: "R1",
    pillar: "Growth + Financial Intelligence",
    stage: "Critical Risk Stage",
    tone: "red",
    profile: "Market Explorer",
    tagline: "You are a Market Explorer, searching for proof that your solution can generate real revenue.",
    sections: [
      {
        title: "Diagnosis Summary",
        blocks: [
          {
            kind: "paragraph",
            text: "You're at the very beginning of your growth and revenue journey. At this stage, you may have an MVP or concept, but you have little to no revenue traction and limited evidence that customers are paying for your solution. This is a high-risk stage because revenue is the ultimate proof of demand. Without revenue or a clear growth engine, investors will see your business as untested. But it's also a stage of maximum learning opportunity, you get to test, pivot, and discover what customers truly want. You are a Market Explorer, searching for proof that your solution can generate real revenue.",
          },
        ],
      },
      {
        title: "Why You Landed Here",
        blocks: [
          { kind: "paragraph", text: "Your responses point to these signals:" },
          {
            kind: "bullets",
            items: [
              "No meaningful or consistent revenue streams yet.",
              "Customer acquisition is ad hoc or anecdotal.",
              "No structured sales funnel, pricing model, or marketing channels.",
              "Financials are basic or non-existent, you may not yet track CAC, LTV, or churn.",
            ],
          },
          {
            kind: "paragraph",
            text: "This means your product may work, but the business model is still unproven.",
          },
        ],
      },
      {
        title: "Strategic Priorities (Next 90 Days)",
        blocks: [
          {
            kind: "steps",
            items: [
              {
                title: "Define Your First Revenue Model",
                sub: [
                  "Clarify how you plan to make money (subscriptions, transaction fees, licensing).",
                  "Test pricing with early users, even small payments are valuable proof.",
                  "Avoid perfection, aim for \"good enough to test.\"",
                ],
              },
              {
                title: "Run Early Go-to-Market Experiments",
                sub: [
                  "Identify one or two channels (direct outreach, communities, LinkedIn).",
                  "Run small campaigns with measurable outcomes.",
                  "Document what works and what doesn't.",
                ],
              },
              {
                title: "Track Basic Growth Metrics",
                sub: [
                  "Begin tracking CAC (Customer Acquisition Cost), LTV (Lifetime Value), and churn.",
                  "Use a simple spreadsheet, precision matters less than habit-building.",
                  "Establish benchmarks for future investor conversations.",
                ],
              },
            ],
          },
        ],
      },
      {
        title: "Tools & Templates to Unlock",
        blocks: [
          {
            kind: "paragraph",
            text: "Your platform unlocks foundational growth tools for this stage:",
          },
          {
            kind: "bullets",
            items: [
              "Growth Hub Lite, funnel template + early SEO checklist",
              "Revenue Model Builder, map pricing + monetization options",
              "Customer Acquisition Experiment Log, simple template to track channel tests",
              "Early Metrics Tracker (Excel), CAC/LTV/churn basics",
            ],
          },
        ],
      },
      {
        title: "What This Means for You",
        blocks: [
          {
            kind: "paragraph",
            text: "Investors don't fund ideas, they fund traction. Your mission is to prove that people will pay for what you're building. This is not about scaling or perfection, it's about evidence. Even $500 in early revenue, if repeatable, is worth more than a hundred conversations about \"future potential.\" Think of yourself as a scientist: your product is the hypothesis, and revenue is the experiment.",
          },
        ],
      },
      {
        title: "Call to Action",
        blocks: [
          { kind: "callout", text: "You are a Market Explorer.", tone: "blue" },
          {
            kind: "paragraph",
            text: "Your job now is to turn customer interest into paying customers.",
          },
          {
            kind: "bullets",
            items: [
              "Use the Revenue Model Builder to define your monetization strategy.",
              "Launch two small GTM experiments this month and measure results.",
              "Track CAC/LTV using the Early Metrics Tracker.",
              "Book a Revenue Discovery Session with a Conncct advisor to refine your GTM.",
            ],
          },
          {
            kind: "callout",
            text: "Your next milestone: progress to G3R2, Revenue Taster, where you begin to see your first revenue signals and early sales traction.",
            tone: "green",
          },
        ],
      },
    ],
  },
  {
    key: "G3R2",
    group: "G3",
    rCode: "R2",
    pillar: "Growth + Financial Intelligence",
    stage: "Very High Risk Stage",
    tone: "orange",
    profile: "Revenue Taster",
    tagline: "You are a Revenue Taster, you've had your first taste of revenue, and now your job is to make it stick.",
    sections: [
      {
        title: "Diagnosis Summary",
        blocks: [
          {
            kind: "paragraph",
            text: "Congratulations, you've crossed an important milestone: your business has started to generate revenue. Even if it's small, inconsistent, or experimental, this is proof that customers are willing to pay for your solution. At this stage, however, the risk remains very high. Revenue is not yet repeatable, and your growth strategy is still in early testing. The key now is to learn from every sale and begin building the systems that can turn early wins into sustainable traction. You are a Revenue Taster, you've had your first taste of revenue, and now your job is to make it stick.",
          },
        ],
      },
      {
        title: "Why You Landed Here",
        blocks: [
          { kind: "paragraph", text: "Your answers point to these markers:" },
          {
            kind: "bullets",
            items: [
              "You have some paying customers, but revenue is inconsistent.",
              "Early traction may come from personal networks or referrals, not structured channels.",
              "Your sales process isn't defined, every deal feels custom.",
              "Financial tracking is minimal, you may know top-line revenue but not CAC, LTV, or margins.",
            ],
          },
          {
            kind: "paragraph",
            text: "This is progress, but it's still fragile. Investors will say: \"Nice start, now prove it's repeatable.\"",
          },
        ],
      },
      {
        title: "Strategic Priorities (Next 90 Days)",
        blocks: [
          {
            kind: "steps",
            items: [
              {
                title: "Systematize Customer Acquisition",
                sub: [
                  "Document how your first sales happened.",
                  "Identify repeatable steps and turn them into a simple process.",
                  "Test 1-2 low-cost channels for lead generation.",
                ],
              },
              {
                title: "Formalize Pricing & Packaging",
                sub: [
                  "Move away from one-off deals into clear pricing tiers.",
                  "Test what customers are willing to pay for and refine accordingly.",
                  "Ensure pricing reflects value, not just cost recovery.",
                ],
              },
              {
                title: "Track Key Financial Metrics",
                sub: [
                  "Start monitoring CAC (Customer Acquisition Cost) and churn.",
                  "Calculate simple gross margins (Revenue - COGS).",
                  "Document retention patterns (do customers come back?).",
                ],
              },
            ],
          },
        ],
      },
      {
        title: "Tools & Templates to Unlock",
        blocks: [
          {
            kind: "paragraph",
            text: "Your platform unlocks early traction tools for this stage:",
          },
          {
            kind: "bullets",
            items: [
              "Early GTM Templates, simple playbooks for outbound, inbound, or community-driven sales",
              "Revenue Checklist, step-by-step validation of pricing, payment, and retention",
              "Sales Process Tracker, standardize acquisition into steps",
              "Basic Financial Dashboard (Excel), monitor CAC, churn, and margins",
            ],
          },
        ],
      },
      {
        title: "What This Means for You",
        blocks: [
          {
            kind: "paragraph",
            text: "Revenue is the strongest signal in startup life, you've taken a critical first step. But early revenue can be misleading if it comes only from friends, discounts, or one-off deals. Your challenge now is to move from \"someone paid me\" to \"customers consistently pay me.\" The sooner you document and refine your process, the sooner you'll have the confidence of both investors and yourself.",
          },
        ],
      },
      {
        title: "Call to Action",
        blocks: [
          { kind: "callout", text: "You are a Revenue Taster.", tone: "blue" },
          {
            kind: "paragraph",
            text: "You've proven that your idea has monetary value. Now you need to turn that spark into a flame.",
          },
          {
            kind: "bullets",
            items: [
              "Use the Revenue Checklist to validate your pricing and retention.",
              "Document your first 5 customer journeys in the Sales Process Tracker.",
              "Set up your Basic Financial Dashboard to track revenue KPIs.",
              "Book a Traction Acceleration Session with a Conncct advisor to build your repeatable sales motion.",
            ],
          },
          {
            kind: "callout",
            text: "Your next milestone: progress to G3R3, Early Seller, where customer acquisition starts becoming repeatable and investors begin to recognize your commercial potential.",
            tone: "green",
          },
        ],
      },
    ],
  },
  {
    key: "G3R3",
    group: "G3",
    rCode: "R3",
    pillar: "Growth + Financial Intelligence",
    stage: "High Risk Stage",
    tone: "orange",
    profile: "Early Seller",
    tagline: "You are an Early Seller, you've proven demand at a small scale, and now it's about turning those wins into a foundation for growth.",
    sections: [
      {
        title: "Diagnosis Summary",
        blocks: [
          {
            kind: "paragraph",
            text: "You've progressed from one-off revenue experiments into the early stages of repeatable sales. Customers are paying you more than once, you're experimenting with channels, and you're starting to see early signs of traction. But you are still in a high-risk zone: your growth is not yet predictable, and your sales process is more art than science. At this stage, the danger is scaling too soon or mistaking early wins for sustainable momentum. Your mission now is to stabilize and standardize your sales motion. You are an Early Seller, you've proven demand at a small scale, and now it's about turning those wins into a foundation for growth.",
          },
        ],
      },
      {
        title: "Why You Landed Here",
        blocks: [
          {
            kind: "paragraph",
            text: "Your diagnostic results highlight these key signals:",
          },
          {
            kind: "bullets",
            items: [
              "You've moved beyond first sales, customers are coming back, or new ones are signing up through a semi-consistent process.",
              "You're experimenting with multiple acquisition channels (outbound, inbound, referrals), but ROI is not clear yet.",
              "Pricing is set but may still be negotiated case by case.",
              "Metrics like CAC and LTV are tracked loosely, not systematically.",
              "Growth feels promising, but fragile.",
            ],
          },
          {
            kind: "paragraph",
            text: "This shows you've taken the right steps, but your business is not yet scalable or investor-grade.",
          },
        ],
      },
      {
        title: "Strategic Priorities (Next 90 Days)",
        blocks: [
          {
            kind: "steps",
            items: [
              {
                title: "Standardize Your Sales Process",
                sub: [
                  "Document your acquisition funnel: awareness, interest, conversion, retention.",
                  "Define what's working and cut what's not.",
                  "Build basic sales playbooks for outreach, demos, and onboarding.",
                ],
              },
              {
                title: "Strengthen Retention & Monetization",
                sub: [
                  "Focus on customer onboarding and success.",
                  "Track churn closely, even 10 early drop-offs are signals.",
                  "Test upsells or cross-sells to increase revenue per customer.",
                ],
              },
              {
                title: "Build a Metrics Discipline",
                sub: [
                  "Measure CAC and LTV with consistency.",
                  "Monitor gross margin and payback periods.",
                  "Track revenue growth monthly, aim for consistency over volume.",
                ],
              },
            ],
          },
        ],
      },
      {
        title: "Tools & Templates to Unlock",
        blocks: [
          {
            kind: "paragraph",
            text: "At this stage, your platform unlocks sales process and metrics tools:",
          },
          {
            kind: "bullets",
            items: [
              "Sales Playbook Lite, scripts and templates for calls, emails, and demos",
              "CRM Starter Template, track prospects, deals, and conversion rates",
              "Revenue KPI Dashboard, monitor CAC, LTV, churn, and revenue growth",
              "Customer Success Checklist, ensure onboarding drives retention",
            ],
          },
        ],
      },
      {
        title: "What This Means for You",
        blocks: [
          {
            kind: "paragraph",
            text: "You're no longer testing whether customers will pay, you know they will. The question now is how to make sales repeatable. Scaling prematurely could collapse your progress; stabilizing your process will set you up for sustainable growth. Investors will be more interested once you can show repeatability and improving economics.",
          },
        ],
      },
      {
        title: "Call to Action",
        blocks: [
          { kind: "callout", text: "You are an Early Seller.", tone: "blue" },
          {
            kind: "paragraph",
            text: "You've proven demand exists, now your job is to make it repeatable.",
          },
          {
            kind: "bullets",
            items: [
              "Document your funnel in the Sales Playbook Lite.",
              "Set up the CRM Starter Template to track deals.",
              "Monitor churn and CAC/LTV with the Revenue KPI Dashboard.",
              "Book a Growth Process Review with a Conncct advisor to stabilize your sales motion.",
            ],
          },
          {
            kind: "callout",
            text: "Your next milestone: progress to G3R4, Revenue Builder, where traction becomes consistent, and your growth story begins to resonate strongly with investors.",
            tone: "green",
          },
        ],
      },
    ],
  },
  {
    key: "G3R4",
    group: "G3",
    rCode: "R4",
    pillar: "Growth + Financial Intelligence",
    stage: "Medium Risk Stage",
    tone: "yellow",
    profile: "Revenue Builder",
    tagline: "You are a Revenue Builder, you've built the early foundation of growth, and now it's about optimizing, strengthening, and scaling it.",
    sections: [
      {
        title: "Diagnosis Summary",
        blocks: [
          {
            kind: "paragraph",
            text: "You've crossed the line from early experiments into consistent traction. Customers are buying, revenue is coming in regularly, and you've started to build a repeatable growth engine. At this stage, the risk is no longer whether customers want your product, it's whether you can scale it efficiently and profitably. Investors will now start taking you seriously, provided you can show healthy unit economics and disciplined financial tracking. You are a Revenue Builder, you've built the early foundation of growth, and now it's about optimizing, strengthening, and scaling it.",
          },
        ],
      },
      {
        title: "Why You Landed Here",
        blocks: [
          {
            kind: "paragraph",
            text: "Your diagnostic responses point to the following:",
          },
          {
            kind: "bullets",
            items: [
              "Consistent revenue is flowing in, with signs of repeat customers or monthly recurring revenue (MRR).",
              "Acquisition channels are clearer, you know where leads are coming from, though ROI may still vary.",
              "Pricing and packaging are set, with fewer one-off deals.",
              "You are starting to track CAC, LTV, and churn, but systems may still be lightweight.",
              "Growth feels structured but not yet optimized.",
            ],
          },
          {
            kind: "paragraph",
            text: "This is a strong stage to be in, but still medium-risk if financial discipline is weak.",
          },
        ],
      },
      {
        title: "Strategic Priorities (Next 90 Days)",
        blocks: [
          {
            kind: "steps",
            items: [
              {
                title: "Strengthen Growth Repeatability",
                sub: [
                  "Focus on the 1-2 channels delivering the best ROI.",
                  "Build structured playbooks for acquisition, onboarding, and customer success.",
                  "Improve conversion rates across your funnel.",
                ],
              },
              {
                title: "Professionalize Financial Tracking",
                sub: [
                  "Move beyond spreadsheets into dashboards or light accounting tools.",
                  "Track CAC vs LTV consistently and report monthly.",
                  "Monitor burn rate and runway closely.",
                ],
              },
              {
                title: "Refine Monetization",
                sub: [
                  "Test pricing elasticity, can you increase ARPU (average revenue per user)?",
                  "Add upsells or add-ons to improve LTV.",
                  "Benchmark your revenue model against industry peers.",
                ],
              },
            ],
          },
        ],
      },
      {
        title: "Tools & Templates to Unlock",
        blocks: [
          {
            kind: "paragraph",
            text: "At this stage, your platform unlocks professionalization resources:",
          },
          {
            kind: "bullets",
            items: [
              "Growth Strategy Hub, playbooks for scaling marketing & sales",
              "KPI Tracker Pro, dashboards for CAC, LTV, MRR, churn, and runway",
              "Pricing Strategy Toolkit, templates for testing and refining pricing",
              "Cash Flow Forecaster, ensure runway aligns with growth plans",
            ],
          },
        ],
      },
      {
        title: "What This Means for You",
        blocks: [
          {
            kind: "paragraph",
            text: "You're in a solid position, your business has traction, customers, and revenue. But consistency is not the same as profitability. At this stage, financial intelligence becomes as important as growth. Investors are watching closely for founders who understand their numbers and can prove that growth is sustainable. Weak financials can overshadow good traction; strong financial discipline can elevate you to the next tier.",
          },
        ],
      },
      {
        title: "Call to Action",
        blocks: [
          { kind: "callout", text: "You are a Revenue Builder.", tone: "blue" },
          {
            kind: "paragraph",
            text: "You've proven customers will pay consistently. Now you must show that growth is scalable and financially sound.",
          },
          {
            kind: "bullets",
            items: [
              "Double down on your top-performing channel with the Growth Strategy Hub.",
              "Track CAC/LTV monthly in the KPI Tracker Pro.",
              "Use the Pricing Strategy Toolkit to refine monetization.",
              "Book a Financial Health Review with a Conncct advisor to align growth with runway.",
            ],
          },
          {
            kind: "callout",
            text: "Your next milestone: progress to G3R5, Growth Driver, where traction becomes strong, unit economics improve, and investors begin to view your company as a serious growth story.",
            tone: "green",
          },
        ],
      },
    ],
  },
  {
    key: "G3R5",
    group: "G3",
    rCode: "R5",
    pillar: "Growth + Financial Intelligence",
    stage: "Semi-Ready Stage",
    tone: "blue",
    profile: "Growth Driver",
    tagline: "You are a Growth Driver, a founder steering the business from early traction toward sustainable, investor-grade growth.",
    sections: [
      {
        title: "Diagnosis Summary",
        blocks: [
          {
            kind: "paragraph",
            text: "You've evolved from steady revenue into accelerating growth. Customers are adopting your product at a faster rate, your acquisition channels are becoming repeatable, and your financial tracking is improving. At this stage, you've shifted from proving the model to scaling it. The key challenge now is ensuring your growth engine runs efficiently: scaling without burning too much cash, while improving unit economics and customer lifetime value. You are a Growth Driver, a founder steering the business from early traction toward sustainable, investor-grade growth.",
          },
        ],
      },
      {
        title: "Why You Landed Here",
        blocks: [
          {
            kind: "paragraph",
            text: "Your diagnostic answers reveal several indicators:",
          },
          {
            kind: "bullets",
            items: [
              "Revenue growth is steady and accelerating month-over-month.",
              "Customer acquisition is becoming predictable, with 1-2 channels working consistently.",
              "CAC and LTV are tracked and starting to look healthy, though margins may still need refinement.",
              "Churn is visible and being managed, but not yet optimized.",
              "Financial discipline is in place, but still founder-led rather than system-led.",
            ],
          },
          {
            kind: "paragraph",
            text: "This is strong progress, but investors will now expect evidence of scale readiness.",
          },
        ],
      },
      {
        title: "Strategic Priorities (Next 90 Days)",
        blocks: [
          {
            kind: "steps",
            items: [
              {
                title: "Optimize Unit Economics",
                sub: [
                  "Lower CAC through deeper channel optimization.",
                  "Increase LTV with upsells, bundles, or expanded offerings.",
                  "Improve gross margin by tightening COGS and ops.",
                ],
              },
              {
                title: "Strengthen Growth Infrastructure",
                sub: [
                  "Move from lightweight CRM to structured sales & marketing tools.",
                  "Automate reporting for CAC, LTV, churn, and margins.",
                  "Build repeatable playbooks for onboarding and customer success.",
                ],
              },
              {
                title: "Prepare for Investor-Grade Reporting",
                sub: [
                  "Start generating monthly growth reports (revenue, KPIs, unit economics).",
                  "Highlight key momentum metrics (YoY growth, MRR, retention cohorts).",
                  "Benchmark against industry standards.",
                ],
              },
            ],
          },
        ],
      },
      {
        title: "Tools & Templates to Unlock",
        blocks: [
          {
            kind: "paragraph",
            text: "At this stage, your platform unlocks scaling + financial rigor tools:",
          },
          {
            kind: "bullets",
            items: [
              "Sales Playbook Pro, campaign library, funnel optimization strategies",
              "Advanced KPI Tracker, dashboards for growth, retention, and unit economics",
              "Forecast Model Template, connect growth to financial runway",
              "Churn Reduction Toolkit, frameworks to analyze and improve retention",
            ],
          },
        ],
      },
      {
        title: "What This Means for You",
        blocks: [
          {
            kind: "paragraph",
            text: "You've proven the model and are now scaling it. The danger at this stage is burning cash faster than you can sustain growth. Growth without efficiency may impress in the short term but fails in due diligence. The founders who win here are those who show not only top-line growth but also discipline in their financial intelligence. Investors love founders who understand both the story and the numbers.",
          },
        ],
      },
      {
        title: "Call to Action",
        blocks: [
          { kind: "callout", text: "You are a Growth Driver.", tone: "blue" },
          {
            kind: "paragraph",
            text: "Your traction is accelerating, now it's about making growth efficient and investor-ready.",
          },
          {
            kind: "bullets",
            items: [
              "Use the Sales Playbook Pro to optimize campaigns and funnels.",
              "Automate your KPI dashboards with the Advanced Tracker.",
              "Build a Forecast Model to connect growth with financial needs.",
              "Schedule a Growth Efficiency Review with a Conncct advisor to align growth and runway.",
            ],
          },
          {
            kind: "callout",
            text: "Your next milestone: progress to G3R6, Financially Disciplined, where growth and financial rigor align to make you fully investor-ready.",
            tone: "green",
          },
        ],
      },
    ],
  },
  {
    key: "G3R6",
    group: "G3",
    rCode: "R6",
    pillar: "Growth + Financial Intelligence",
    stage: "Investor-Ready Stage",
    tone: "green",
    profile: "Financially Disciplined",
    tagline: "You are Financially Disciplined, a founder who balances growth ambition with financial control, making your company a strong candidate for investment.",
    sections: [
      {
        title: "Diagnosis Summary",
        blocks: [
          {
            kind: "paragraph",
            text: "You've reached a critical milestone: your startup now combines traction with discipline. Revenue is growing consistently, customers are retained, and your financial metrics are clear, structured, and defensible. At this stage, you're no longer just proving that growth exists, you're showing that it's sustainable, efficient, and attractive to investors. You've entered the territory where investors actively view you as \"fundable.\" You are Financially Disciplined, a founder who balances growth ambition with financial control, making your company a strong candidate for investment.",
          },
        ],
      },
      {
        title: "Why You Landed Here",
        blocks: [
          {
            kind: "paragraph",
            text: "Your diagnostic results show these markers of readiness:",
          },
          {
            kind: "bullets",
            items: [
              "Revenue is consistent and scaling, with repeatable acquisition channels.",
              "Unit economics (CAC, LTV, churn, gross margin) are measured regularly and trending positively.",
              "Burn rate and runway are tracked and tied to operational plans.",
              "Monthly reporting exists (MRR/ARR, growth %, churn).",
              "Financial data is accurate and defensible, you can back your claims with numbers.",
            ],
          },
          {
            kind: "paragraph",
            text: "This means you are investor-ready from a financial intelligence standpoint.",
          },
        ],
      },
      {
        title: "Strategic Priorities (Next 90 Days)",
        blocks: [
          {
            kind: "steps",
            items: [
              {
                title: "Institutionalize Reporting",
                sub: [
                  "Move from founder-led tracking to a proper FP&A (financial planning & analysis) cadence.",
                  "Automate dashboards for growth + financial KPIs.",
                  "Ensure reporting is aligned with investor expectations (MRR/ARR, cohorts, margins).",
                ],
              },
              {
                title: "Strengthen Forecasting",
                sub: [
                  "Build multi-scenario forecasts (base, upside, downside).",
                  "Align financial plans with hiring, GTM, and runway.",
                  "Stress-test assumptions before investor meetings.",
                ],
              },
              {
                title: "Prepare for Investor Engagement",
                sub: [
                  "Ensure your deck integrates financial KPIs clearly.",
                  "Create an \"Investor Snapshot\", one-pager summarizing traction and unit economics.",
                  "Run mock investor sessions to practice defending numbers.",
                ],
              },
            ],
          },
        ],
      },
      {
        title: "Tools & Templates to Unlock",
        blocks: [
          {
            kind: "paragraph",
            text: "At this stage, your platform unlocks investor-grade financial tools:",
          },
          {
            kind: "bullets",
            items: [
              "Investor-Ready Forecast Model, detailed, multi-scenario Excel template",
              "Monthly Reporting Pack, MRR/ARR, cohorts, CAC/LTV, churn analysis",
              "Investor Snapshot Template, one-page summary for outreach",
              "Deal Room Audit Guide, ensure financial docs are diligence-ready",
            ],
          },
        ],
      },
      {
        title: "What This Means for You",
        blocks: [
          {
            kind: "paragraph",
            text: "You are no longer just growing, you are managing growth intelligently. This is what separates investable startups from risky ones. The biggest risk now is complacency. If growth slows or reporting lapses, investor confidence can fade quickly. Your job is to maintain rigor while continuing to scale.",
          },
        ],
      },
      {
        title: "Call to Action",
        blocks: [
          { kind: "callout", text: "You are Financially Disciplined.", tone: "blue" },
          {
            kind: "paragraph",
            text: "Your growth story and financials are aligned, investors will take you seriously.",
          },
          {
            kind: "bullets",
            items: [
              "Automate your reporting with the Monthly Reporting Pack.",
              "Build multi-scenario forecasts using the Investor-Ready Forecast Model.",
              "Prepare your Investor Snapshot for fundraising conversations.",
              "Book a Capital Readiness Session with a Conncct advisor to finalize fundraising prep.",
            ],
          },
          {
            kind: "callout",
            text: "Your next milestone: progress to G3R7, Growth Leader, where your financial discipline and growth trajectory make you a standout company attracting top-tier investors.",
            tone: "green",
          },
        ],
      },
    ],
  },
  {
    key: "G3R7",
    group: "G3",
    rCode: "R7",
    pillar: "Growth + Financial Intelligence",
    stage: "Exceptional Stage",
    tone: "green",
    profile: "Growth Leader",
    tagline: "You are a Growth Leader, a founder whose growth engine is proven, scalable, and positioned for long-term success.",
    sections: [
      {
        title: "Diagnosis Summary",
        blocks: [
          {
            kind: "paragraph",
            text: "You've reached the pinnacle of growth and financial discipline. Your company is not just generating consistent revenue, it's scaling rapidly, efficiently, and in a way that demonstrates clear market leadership potential. Investors will now view you as an exceptional opportunity: your traction is strong, your unit economics are attractive, and your financial reporting is investor-grade. You're no longer pitching for capital, you're selecting the right investors and negotiating from a position of strength. You are a Growth Leader, a founder whose growth engine is proven, scalable, and positioned for long-term success.",
          },
        ],
      },
      {
        title: "Why You Landed Here",
        blocks: [
          { kind: "paragraph", text: "Your diagnostic results show:" },
          {
            kind: "bullets",
            items: [
              "Strong, accelerating revenue growth (MRR/ARR scaling consistently).",
              "Unit economics are healthy, CAC is under control, LTV is high, margins are attractive.",
              "Churn is managed and retention is strong across customer cohorts.",
              "Forecasts are realistic and credible, with multi-scenario models in place.",
              "Investor-grade reporting is consistent, accurate, and defensible.",
              "You're starting to attract inbound investor interest and potential strategic partners.",
            ],
          },
          {
            kind: "paragraph",
            text: "This is the level where founders stand out in the eyes of both investors and markets.",
          },
        ],
      },
      {
        title: "Strategic Priorities (Next 90 Days)",
        blocks: [
          {
            kind: "steps",
            items: [
              {
                title: "Leverage Investor Demand",
                sub: [
                  "Run a structured, time-bound fundraising process to maximize momentum.",
                  "Choose investors strategically, prioritize those offering networks, expertise, or global reach.",
                  "Negotiate terms that protect founder ownership and governance.",
                ],
              },
              {
                title: "Position for Scaling Beyond Growth",
                sub: [
                  "Expand into new markets or verticals.",
                  "Invest in scaling operations (sales, marketing, customer success).",
                  "Prepare your org chart and leadership team for Series A/B scaling.",
                ],
              },
              {
                title: "Institutionalize Financial Governance",
                sub: [
                  "Move toward quarterly board-level reporting.",
                  "Prepare for audits and compliance requirements.",
                  "Build an internal finance function (CFO, FP&A lead).",
                ],
              },
            ],
          },
        ],
      },
      {
        title: "Tools & Templates to Unlock",
        blocks: [
          {
            kind: "paragraph",
            text: "At this stage, your platform unlocks elite-level scaling and investor resources:",
          },
          {
            kind: "bullets",
            items: [
              "Investor Growth Readiness Package, benchmark against Series A/B expectations",
              "Global Expansion Playbook, frameworks for entering new markets",
              "Governance & Board Toolkit, board structure, ESOP, compliance, ESG readiness",
              "Strategic Investor Selection Guide, frameworks for choosing investors beyond money",
            ],
          },
        ],
      },
      {
        title: "What This Means for You",
        blocks: [
          {
            kind: "paragraph",
            text: "You're not just proving growth, you're leading it. You have the discipline, traction, and financial clarity that make you a category-defining company in the making. Your challenge now is less about raising money and more about choosing wisely: the wrong investor can slow you down, while the right one can propel you to market leadership.",
          },
        ],
      },
      {
        title: "Call to Action",
        blocks: [
          { kind: "callout", text: "You are a Growth Leader.", tone: "blue" },
          {
            kind: "paragraph",
            text: "You've built a proven, scalable growth engine and strong financial discipline. Now it's time to scale with strategic capital and partners.",
          },
          {
            kind: "bullets",
            items: [
              "Launch a structured raise with the Investor Growth Readiness Package.",
              "Use the Global Expansion Playbook to assess new market opportunities.",
              "Formalize governance with the Board & Governance Toolkit.",
              "Book a Strategic Capital Partnering Session with a Conncct advisor to select the right investors.",
            ],
          },
          {
            kind: "callout",
            text: "Your next frontier: becoming a market leader, raising from top-tier funds, and positioning for international expansion and long-term category dominance.",
            tone: "green",
          },
        ],
      },
    ],
  },
];
